import type {
  AccountConsistencyConfig,
  ConsistencyMode,
} from '@fragment/types/schemas';
import { AccountConsistencyConfigSchema } from '@fragment/types/schemas';

import type { ValidationError, ValidationResult } from './fieldErrorHelpers';
import { LedgerConsistencyConfigSchema } from '../types';

/**
 * Determines if a schema is a totalBalance schema based on defaultConsistencyConfig.
 */
type TotalBalanceDefaultConsistencyConfig = Pick<
  AccountConsistencyConfig,
  'totalBalanceUpdates'
> & {
  totalBalanceUpdates: ConsistencyMode;
};

export const isTotalBalanceSchema = (
  defaultConsistencyConfig:
    | Pick<AccountConsistencyConfig, 'totalBalanceUpdates'>
    | undefined
): defaultConsistencyConfig is TotalBalanceDefaultConsistencyConfig => {
  return defaultConsistencyConfig?.totalBalanceUpdates !== undefined;
};

type BalanceUpdatesConfig = Pick<
  AccountConsistencyConfig,
  'ownBalanceUpdates' | 'totalBalanceUpdates'
>;

/**
 * An account's effective balance-updates consistency: its own setting, falling
 * back to the schema default for the schema's balance flavor. The runtime
 * account config map layers its env-driven fallback on top of this. Without a
 * schema default the flavor is unknown, so the account's settings pass through
 * as-is.
 */
export const resolveBalanceUpdatesConsistency = ({
  accountConsistencyConfig,
  defaultConsistencyConfig,
}: {
  accountConsistencyConfig: BalanceUpdatesConfig | undefined;
  defaultConsistencyConfig: BalanceUpdatesConfig | undefined;
}): BalanceUpdatesConfig => {
  if (defaultConsistencyConfig === undefined) {
    return {
      ownBalanceUpdates: accountConsistencyConfig?.ownBalanceUpdates,
      totalBalanceUpdates: accountConsistencyConfig?.totalBalanceUpdates,
    };
  }
  return isTotalBalanceSchema(defaultConsistencyConfig)
    ? {
        totalBalanceUpdates:
          accountConsistencyConfig?.totalBalanceUpdates ??
          defaultConsistencyConfig.totalBalanceUpdates,
      }
    : {
        ownBalanceUpdates:
          accountConsistencyConfig?.ownBalanceUpdates ??
          defaultConsistencyConfig.ownBalanceUpdates,
      };
};

// Ledger consistency validation input type
export type LedgerConsistencyValidationInput = {
  entries?: ConsistencyMode;
};

/**
 * Validates account-level consistency settings (lines and balance)
 * Uses AccountConsistencyConfigSchema for validation
 */
export const validateAccountConsistencySettings = ({
  lines,
  ownBalanceUpdates,
  totalBalanceUpdates,
  groups,
}: AccountConsistencyConfig): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate account consistency config
  const validationResult = AccountConsistencyConfigSchema.safeParse({
    lines,
    ownBalanceUpdates,
    totalBalanceUpdates,
    groups,
  });

  if (!validationResult.success) {
    validationResult.error.errors.forEach((error) => {
      errors.push({
        message: error.message,
        path: error.path,
      });
    });
  }

  return {
    errors,
  };
};

/**
 * Validates ledger-level consistency settings (entries)
 * Uses LedgerConsistencyConfigSchema for validation
 */
export const validateLedgerConsistencySettings = ({
  entries,
}: LedgerConsistencyValidationInput): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate ledger consistency config
  const validationResult = LedgerConsistencyConfigSchema.safeParse({
    entries,
  });

  if (!validationResult.success) {
    validationResult.error.errors.forEach((error) => {
      errors.push({
        message: error.message,
        path: error.path,
      });
    });
  }

  return {
    errors,
  };
};
